/**
 * CSV of the current grid (§A PR-6).
 *
 * **The values come from the server-returned row objects, never from a
 * re-fetch and never from any "reveal" path.** Since Phase 08 §B4 the API
 * masks identifiers at the boundary, so a CSV built from the rows the page is
 * already showing exports masked values *by construction* — there is no code
 * here that could unmask anything, which is a stronger guarantee than a
 * masking step that could be forgotten.
 *
 * Quoting is RFC 4180: a field containing a comma, a double quote, or a line
 * break is wrapped in double quotes, and embedded double quotes are doubled.
 * Records are joined with CRLF because that is what the RFC specifies and what
 * every spreadsheet on every platform opens without a shrug.
 */

export interface CsvColumn<T> {
  header: string;
  /** `null`/`undefined` become an empty field, mirroring the tables' `—`. */
  value(row: T): string | number | boolean | null | undefined;
}

/** One field, quoted only when RFC 4180 requires it. */
function cell(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return '';
  const text = String(value);
  // Quote when the field contains a comma, a quote, or either line-break
  // character — CR and LF separately, because a lone `\r` pasted from a
  // spreadsheet is still a record boundary to a strict reader.
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function toCsv<T>(columns: readonly CsvColumn<T>[], rows: readonly T[]): string {
  const lines = [columns.map((col) => cell(col.header)).join(',')];
  for (const row of rows) {
    lines.push(columns.map((col) => cell(col.value(row))).join(','));
  }
  // Trailing CRLF: RFC 4180 permits either, but a final unterminated record
  // trips some strict importers, so the file ends the way every line does.
  return `${lines.join('\r\n')}\r\n`;
}

/**
 * Hands the CSV to the browser's download manager — the `ExportsPage.tsx`
 * Blob/anchor/revoke choreography, for the same reasons: a plain `<a href>`
 * cannot exist because there is no URL (the bytes are client-built), and the
 * object URL is revoked immediately because the click has already handed the
 * blob over and an un-revoked URL pins it in memory for the life of the page.
 */
export function downloadCsv(fileName: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}
